/**
 * @format
 */
import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { SelectedElement, StyleRecord, Styles } from "../types/editor-types";
import { BaseToolService } from "./base-tool.service";

@Injectable()
export class StylesService extends BaseToolService {
   selectedStylesSubject$ = new BehaviorSubject<Styles>({
      isBold: false,
      isUnderLine: false,
      fontFamily: undefined,
      fontSize: "8px",
      fontColor: "",
      textAlignment: "",
   });

   private recordStyles: Set<StyleRecord>;
   private styleAndValue: Partial<Record<StyleRecord, string>>;

   private isBold(): boolean {
      return this.recordStyles.has("bold");
   }

   private isUnderLine(): boolean {
      return this.recordStyles.has("underline");
   }

   private getAnalyzeStyle(styleRecordType: StyleRecord): string | null {
      if (this.recordStyles.has(styleRecordType)) {
         return this.styleAndValue[styleRecordType];
      }
      return null;
   }

   analyzeAllAppliedStyles(selection: Partial<SelectedElement>): void {
      try {
         this.resetStyleState();

         if (selection.isParameter) {
            this.analyzeComputedStyles(selection.parameterParentElement);
         } else {
            this.analyzeComputedStyles(selection.anchorNode);
         }

         let styleApplied: Styles = {
            isBold: false,
            isUnderLine: false,
            fontFamily: "",
            fontSize: "",
            fontColor: "",
            textAlignment: "",
         };

         styleApplied.isBold = this.isBold();
         styleApplied.isUnderLine = this.isUnderLine();
         styleApplied.fontFamily = this.getAnalyzeStyle("fontFamily");
         styleApplied.fontSize = this.getAnalyzeStyle("fontSize");
         styleApplied.fontColor = this.getAnalyzeStyle("color");
         styleApplied.textAlignment = this.getAnalyzeStyle("alignment");

         this.selectedStylesSubject$.next(styleApplied);
      } catch (error) {
         console.error(`(custom error) catch in 'getAllAppliedStyles' `, error);
      }
   }
   private analyzeComputedStyles(node: Node): void {
      let element: Element;

      if (this.isText(node)) {
         element = <Element>node.parentNode;
      } else {
         element = <Element>node;
      }
      const { textAlign, fontSize, fontFamily, color } = window.getComputedStyle(element);

      this.saveRecordStyles("alignment", textAlign);
      this.saveRecordStyles("fontSize", fontSize);
      this.saveRecordStyles("fontFamily", this.getFirstFontFamily(fontFamily));
      this.saveRecordStyles("color", this.getFontColor(color));

      this.analyzeRecursive(node);
   }

   private analyzeRecursive(node: Node): void {
      if (!this.isText(node) && Boolean((<Element>node).attributes["ng-model"])) {
         return;
      }

      if (this.isB(node)) {
         this.saveRecordStyles("bold", "true");
         this.analyzeRecursive(node.parentNode);
         return;
      }

      if (this.isU(node)) {
         this.saveRecordStyles("underline", "true");
         this.analyzeRecursive(node.parentNode);
         return;
      }

      this.analyzeRecursive(node.parentNode);
   }

   private saveRecordStyles(styleRecorded: StyleRecord, value: string) {
      this.recordStyles.add(styleRecorded);
      this.styleAndValue = { ...this.styleAndValue, [styleRecorded]: value };
   }

   private resetStyleState(): void {
      this.recordStyles = new Set<StyleRecord>();
      this.styleAndValue = {};
   }

   private getFirstFontFamily(fontFamily: string): string {
      return fontFamily.split(",")[0].replace(/"/g, "").trim();
   }

   private getFontColor(fontColorRGB: string): string {
      const defaultColor = "#000000";

      if (!Boolean(fontColorRGB)) {
         return defaultColor;
      }

      const rgbArray = fontColorRGB.replace(/rgb\(/g, "").replace(/\)/g, "").split(",");
      return this.rgbToHex(rgbArray[0], rgbArray[1], rgbArray[2]).replace(/\s/g, "");
   }

   private componentToHex(c: string): string {
      const number: number = +c;
      const hex = number.toString(16);
      return hex.length == 1 ? "0" + hex : hex;
   }

   private rgbToHex(r, g, b): string {
      return "#" + this.componentToHex(r) + this.componentToHex(g) + this.componentToHex(b);
   }
}
